<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal
      :title="$t('Mark Lot(s) Unsold') + ' – ' + selectedItems.length + ' ' + $t('Lot(s)')"
      modal-class="mobile-full-screen"
      @ok="handleSubmit(onSubmit)"
      :content-in-form="false"
      :visible="modal.show"
      @hide="onHidden"
      :disable-submit-btn="disableSubmit || modal.isLoading"
      scrollable
      :size="computedSize">
      <spinner show v-if="modal.isLoading || !initialized"/>
      <div v-else>
        <div v-if="batchUnsoldParams.additional_fields && batchUnsoldParams.additional_fields.length">
          <div v-for="(field, index) in batchUnsoldParams.additional_fields" :key="index" class="row">
            <div class="col-md-4">
              <b-form-group>
                <label :for="'field-' + field.key">{{ field.label }}</label>
                <b-input-group>
                  <input v-model="additionalFields[field.name]" :id="'field-' + field.key"
                         :label="field.label" class="form-control"/>
                </b-input-group>
              </b-form-group>
            </div>
          </div>
        </div>
        <b-table :items="selectedItems" :fields="tableColumns" hover>
          <template slot="cell(lot_no)" slot-scope="{item}">
            <clickable-lot-no :obj="item" is-financial-item></clickable-lot-no>
          </template>

          <template slot="cell(title)" slot-scope="{item}">
            {{ item.inventory.short_description | striptags | truncate(50) }}
          </template>

          <template slot="cell(invoice_status)" slot-scope="{item}">
            <span v-if="getInvoice(item)" v-as-status="getInvoice(item).status"
                  @click="onPdfViewClick(getInvoice(item), true)" class="invoice">
              {{ getInvoice(item).invoice_no }} - {{ getInvoice(item).status | removeUnderscores | toUpperCaseFirst | translate }}
            </span>
            <span v-else v-as-status="'N/A'">{{ $t('N/A') }}</span>
          </template>

          <template slot="cell(invoice_total_amount)" slot-scope="{item}" v-if="getInvoice(item)">
            {{
              getInvoice(item).total_amount | asCurrency(getInvoice(item).currency ? getInvoice(item).currency.symbol : null)
            }}
          </template>

          <template slot="cell(invoice_paid_amount)" slot-scope="{item}" v-if="getInvoice(item)">
            <span v-if="getInvoice(item).invoice_payments.length" v-as-status="'N/A'" style="cursor: pointer;"
              @click="onInvoicePaymentsClick(getInvoice(item))">
            {{
              getInvoice(item).paid_amount | asCurrency(getInvoice(item).currency ? getInvoice(item).currency.symbol : null)
            }}
            </span>
            <span v-else>
            {{
              getInvoice(item).paid_amount | asCurrency(getInvoice(item).currency ? getInvoice(item).currency.symbol : null)
            }}
            </span>
          </template>

          <template slot="cell(settlement_status)" slot-scope="{item}">
            <span v-if="getSettlement(item)" v-as-status="getSettlement(item).status"
                  @click="onPdfViewClick(getSettlement(item), false)" class="settlement">
              {{ getSettlement(item).settlement_no }} - {{
                getSettlement(item).status | removeUnderscores | toUpperCaseFirst | translate
              }}
            </span>
            <span v-else v-as-status="'N/A'">{{ $t('N/A') }}</span>
          </template>

          <template slot="cell(settlement_total_amount)" slot-scope="{item}" v-if="getSettlement(item)">
            {{
              getSettlement(item).total_amount | asCurrency(getSettlement(item).currency ? getSettlement(item).currency.symbol : null)
            }}
          </template>

          <template slot="cell(settlement_paid_amount)" slot-scope="{item}" v-if="getSettlement(item)">
            <span v-if="getSettlement(item).settlement_payments.length" v-as-status="'N/A'" style="cursor: pointer;"
              @click="onSettlementPaymentsClick(getSettlement(item))">
            {{
              getSettlement(item).payments_amount | asCurrency(getSettlement(item).currency ? getSettlement(item).currency.symbol : null)
            }}
            </span>
            <span v-else>
            {{
              getSettlement(item).payments_amount | asCurrency(getSettlement(item).currency ? getSettlement(item).currency.symbol : null)
            }}
            </span>
          </template>

          <template #cell(invoice_actions)="{item}">
            <div v-if="invoiceExist && getInvoice(item)">
              <unsold-modal-table-item @updateData="updateData" :is-invoice="true" :reservation-uuid="item.uuid"
                                       :error-msg="getErrorMsg(item.uuid, true)" :state="getState(item.uuid, true)"
                                       :passed-value="passedValues[item.uuid] || null"/>
            </div>
          </template>

          <template #cell(settlement_actions)="{item}">
            <div v-if="settlementExist && getSettlement(item)">
              <unsold-modal-table-item @updateData="updateData" :is-invoice="false" :reservation-uuid="item.uuid"
                                       :error-msg="getErrorMsg(item.uuid, false)" :state="getState(item.uuid, false)"
                                       :passed-value="settlementPassedValues[item.uuid] || null"/>
            </div>
          </template>

        </b-table>
      </div>
    </artisio-modal>
    <invoice-payment-list-modal/>
    <settlement-payment-list-modal/>
  </ValidationObserver>
</template>
<script>

  import ArtisioModal from "@/core/components/artisio-modal/ArtisioModal.vue";
  import {createNamespacedHelpers} from "vuex";
  import UnsoldModalTableItem from "@/modules/lots/components/UnsoldModalTableItem.vue";
  import {ON_PDF_MODAL_CLOSE, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import InvoicesService from "@/modules/invoices/InvoicesService";
  import LotsService from "@/modules/lots/LotsService";
  import SettlementService from "@/modules/settlements/SettlementService";
  import Spinner from "@/core/components/common/Spinner.vue";
  import ClickableLotNo from "@/modules/lots/clickable-lot-no/ClickableLotNo.vue";
  import {SOURCE_BATCH_MARK_UNSOLD_MODAL} from "@/modules/constants";
  import InvoicePaymentListModal from "@/modules/invoices/PaymentListModal";
  import SettlementPaymentListModal from "@/modules/settlements/PaymentListModal";

  const {mapState, mapActions} = createNamespacedHelpers('lots')
  const {mapState: globalMapState} = createNamespacedHelpers('global');
  const {mapActions: invoiceMapActions} = createNamespacedHelpers('invoices');
  const {mapActions: settlementMapActions} = createNamespacedHelpers('settlements');

  export default {
    name: "UnsoldModal",
    components: {
      ClickableLotNo,
      Spinner,
      ArtisioModal,
      UnsoldModalTableItem,
      InvoicePaymentListModal,
      SettlementPaymentListModal
    },
    data() {
      return {
        data: {},
        errors: {},
        modified: {},
        invSetToResMap: {},
        resToInvSetMap: {invoices: {}, settlements: {}},
        disableSubmit: false,
        initialized: false,
        additionalFields: {},
        passedValues: {},
        settlementPassedValues: {},
      }
    },
    computed: {
      ...mapState({
        modal: state => state.unsoldModal,
        selectedItems: state => state.unsoldModal.selectedItems
      }),
      ...globalMapState({
        batchUnsoldParams: state => state.batchUnsoldParams
      }),
      invoiceExist() {
        for (let res of this.selectedItems) {
          if (res.lot && res.lot.financial_item && res.lot.financial_item.invoice) return true
        }
        return false
      },
      settlementExist() {
        for (let res of this.selectedItems) {
          if (res.lot && res.lot.financial_item && res.lot.financial_item.settlement) return true
        }
        return false
      },
      tableColumns() {
        const baseColumns = [
          {key: 'lot_no', label: this.$i18n.t('Lot')},
          {key: 'title', label: this.$i18n.t('Title')},
          {key: 'invoice_status', label: this.$t('Invoice Status')},
          {key: 'invoice_total_amount', label: this.$t('Invoice Total')},
          {key: 'invoice_paid_amount', label: this.$t('Invoice Paid')},
          {key: 'settlement_status', label: this.$t('Settlement Status')},
          {key: 'settlement_total_amount', label: this.$t('Settlement Total')},
          {key: 'settlement_paid_amount', label: this.$t('Settlement Paid')}
        ]
        if (this.invoiceExist) {
          baseColumns.push({key: 'invoice_actions', label: this.$t('Invoice Actions')})
        }
        if (this.settlementExist) {
          baseColumns.push({key: 'settlement_actions', label: this.$t('Settlement Actions')})
        }
        return baseColumns
      },
      computedSize() {
        if (this.modal.isLoading) {
          return 'l'
        }
        return 'xl'
      }
    },
    methods: {
      ...mapActions(['hideBatchLotUnsoldModal']),
      ...invoiceMapActions({showInvoicePaymentListModal: 'showPaymentListModal'}),
      ...settlementMapActions({showSettlementPaymentListModal: 'showPaymentListModal'}),
      onHidden() {
        this.data = {};
        this.errors = {};
        this.modified = {};
        this.invSetToResMap = {};
        this.resToInvSetMap = {invoices: {}, settlements: {}};
        this.disableSubmit = false;
        this.initialized = false;
        this.additionalFields = {};
        this.passedValues = {};
        this.settlementPassedValues = {};
        this.hideBatchLotUnsoldModal();
      },
      async onSubmit() {
        const isSuccess = await this.$confirm(
          null,
          null,
          null,
          null,
          this.$i18n.t('Are you sure you want to mark selected lots as unsold?'),
          this.$i18n.t('Yes'),
          this.$i18n.t('No'),
          null,
          {
            label: this.$i18n.t("Please type 'confirm' to proceed"),
            text: 'confirm'
          }
        );
        if (!isSuccess) {
          return;
        }
        await this.sendData();
      },
      async sendData() {
        const requestData = {'lots': []}
        for (const [reservation_uuid, choiceData] of Object.entries(this.data)) {
          requestData.lots.push({reservation_uuid, ...choiceData})
        }
        if (this.batchUnsoldParams.additional_fields) {
          Object.assign(requestData, this.additionalFields);
        }
        let res = await LotsService.sendUnsoldInfo(requestData);
        if (res.success) {
          this.hideBatchLotUnsoldModal();
        } else {
          if (res.body.non_field_errors) {
            this.$errorDialog(res.body.non_field_errors[0]);
          } else {
            this.$errorDialog(this.$t('something went wrong'));
          }
        }
      },
      getInvoice(item) {
        if (item.lot && item.lot.financial_item && item.lot.financial_item.invoice) {
          return item.lot.financial_item.invoice;
        }
        return null;
      },
      getSettlement(item) {
        if (item.lot && item.lot.financial_item && item.lot.financial_item.settlement) {
          return item.lot.financial_item.settlement;
        }
        return null;
      },
      async onPdfViewClick(item, isInvoice) {
        const name = isInvoice ? this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: item.invoice_no}) :
          this.$i18n.t(`Settlement {settlement_no}`, {settlement_no: item.settlement_no})
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {title: name});
        let response = isInvoice ? await InvoicesService.getInvoicePdfData(item.uuid) : await SettlementService.getSettlementPdfData(item.uuid)
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {...response.body, filename: name})
        } else {
          this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
          let errorMessage = "";
          if (response.body.non_field_errors) {
            errorMessage = response.body.non_field_errors[0];
          } else {
            errorMessage = response.body;
          }
          this.$errorDialog(errorMessage);
        }
      },
      updateData(data) {
        const {reservation_uuid, isInvoice, option} = data
        this.setData(reservation_uuid, isInvoice, option)
        this.setModified(reservation_uuid, isInvoice, true, option)
        this.validateData()
        this.updateSubmit()
      },
      updateSubmit() {
        for (const reservation_uuid of Object.keys(this.data)) {
          if (this.data[reservation_uuid].invoice_option === null ||
            this.data[reservation_uuid].settlement_option === null ||
            (this.errors[reservation_uuid]['settlement'] && this.errors[reservation_uuid]['settlement'].state === false) ||
            (this.errors[reservation_uuid]['invoice'] && this.errors[reservation_uuid]['invoice'].state === false)) {
            this.disableSubmit = true
            return
          }
        }
        this.disableSubmit = false
      },
      validateData() {
        for (const [reservation_uuid, choiceData] of Object.entries(this.data)) {
          if ('invoice_option' in choiceData) {
            if (this.getModified(reservation_uuid, true)) {
              let errorMsg = null;
              let errorState = true;
              if (choiceData.invoice_option === null) {
                errorState = false;
              } else {
                const inv = this.resToInvSetMap['invoices'][reservation_uuid];
                const invReservations = this.invSetToResMap[inv.uuid];
                if (inv.total === invReservations.size && ['new_invoice', 'remove'].includes(choiceData.invoice_option)) {
                  errorMsg = this.$i18n.t('All sold lots from the invoice are going to be marked as Unsold, therefore this action would leave the invoice empty. Please choose another action');
                  errorState = false;
                }
                if (choiceData.invoice_option === 'cancel_invoice' && inv.paidAmount > 0) {
                  errorMsg = this.$i18n.t('You cannot cancel an invoice with active payments. Cancel the payment first.');
                  errorState = false;
                }
              }
              this.setError(reservation_uuid, 'invoice', {msg: errorMsg, state: errorState});
            }
          }
          if ('settlement_option' in choiceData) {
            let errorMsg = null
            let errorState = true
            const curOpt = choiceData.settlement_option;
            if (curOpt === null) {
              errorState = this.getModified(reservation_uuid, false) ? false : null
            } else {
              const set = this.resToInvSetMap['settlements'][reservation_uuid]
              if (curOpt === 'cancel_settlement' && set.paidAmount > 0) {
                errorMsg = this.$i18n.t('You cannot cancel a settlement  with active payments. Cancel the payment first.');
                errorState = false;
              }
              const setReservations = this.invSetToResMap[set.uuid]
              const removedLotsCount = [...setReservations].filter(resUuid => [
                'new_settlement', 'remove'].includes(this.data[resUuid].settlement_option)).length;
              if (removedLotsCount === set.total) {
                errorMsg = this.$i18n.t('All sold lots from the settlement are going to be marked as Unsold. This action would leave the settlement empty. Please choose another action');
                errorState = false;
              } else {
                const resUuids = [...setReservations].filter(item => item !== reservation_uuid);
                for (const resUuid of resUuids) {
                  const resOpt = this.data[resUuid].settlement_option;
                  if (resOpt && ((curOpt === 'cancel_settlement') !== (resOpt === 'cancel_settlement'))) {
                    errorMsg = this.$i18n.t('Selected actions conflict with each other');
                    errorState = false;
                    break;
                  }
                  if (
                    ['new_settlement_keep_item', 'new_settlement'].includes(curOpt) &&
                    ['debit_note', 'remove', 'keep_item'].includes(resOpt) ||

                    curOpt === 'debit_note' &&
                    ['new_settlement_keep_item', 'new_settlement', 'remove', 'keep_item'].includes(resOpt) ||

                    curOpt === 'remove' &&
                    ['new_settlement_keep_item', 'new_settlement', 'debit_note'].includes(resOpt) ||

                    curOpt === 'keep_item' &&
                    ['new_settlement_keep_item', 'new_settlement', 'debit_note'].includes(resOpt)
                  ) {
                    errorMsg = this.$i18n.t('Selected actions conflict with each other');
                    errorState = false;
                    break;
                  }
                }
              }
            }
            this.setError(reservation_uuid, 'settlement', {msg: errorMsg, state: errorState});
          }
        }
      },
      getErrorMsg(reservation_uuid, isInvoice) {
        return this.errors[reservation_uuid][isInvoice ? 'invoice' : 'settlement'].msg
      },
      getState(reservation_uuid, isInvoice) {
        return this.errors[reservation_uuid][isInvoice ? 'invoice' : 'settlement'].state
      },
      getModified(reservation_uuid, isInvoice) {
        return this.modified[reservation_uuid][isInvoice ? 'invoice' : 'settlement']
      },
      setData(reservation_uuid, isInvoice, value) {
        if (isInvoice) {
          this.data[reservation_uuid]['invoice_option'] = value;
          const related_lots = this.invSetToResMap[this.resToInvSetMap['invoices'][reservation_uuid].uuid];
          let uuids = [reservation_uuid];
          Object.keys(this.data).forEach(key => {
            if (key !== reservation_uuid && related_lots.has(key)) {
              this.data[key]['invoice_option'] = value;
              uuids.push(key);
            }
          });
          const updatedValues = { ...this.passedValues };
          uuids.forEach(uuid => {
            updatedValues[uuid] = value;
          });
          this.$nextTick(() => {
            this.passedValues = updatedValues;
          });
        } else {
          this.data[reservation_uuid]['settlement_option'] = value;
          if (['cancel_settlement', 'debit_note'].includes(value)) {
            const related_lots = this.invSetToResMap[this.resToInvSetMap['settlements'][reservation_uuid].uuid];
            let uuids = [reservation_uuid];
            Object.keys(this.data).forEach(key => {
              if (key !== reservation_uuid && related_lots.has(key)) {
                this.data[key]['settlement_option'] = value;
                uuids.push(key);
              }
            });
            const updatedValues = { ...this.settlementPassedValues };
            uuids.forEach(uuid => {
              updatedValues[uuid] = value;
            });
            this.$nextTick(() => {
              this.settlementPassedValues = updatedValues;
            });
          } else {
            this.settlementPassedValues[reservation_uuid] = value;
          }
        }
      },
      setError(reservation_uuid, action_type, values = {}) {
        this.errors[reservation_uuid][action_type] = values;
      },
      setModified(reservation_uuid, isInvoice, value, option) {
        if (isInvoice) {
          const related_lots = this.invSetToResMap[this.resToInvSetMap['invoices'][reservation_uuid].uuid];
          Object.keys(this.modified).forEach(key => {
            if (related_lots.has(key)) {
              this.modified[key]['invoice'] = value;
            }
          });
        } else {
          if (['cancel_settlement', 'debit_note'].includes(option)) {
            const related_lots = this.invSetToResMap[this.resToInvSetMap['settlements'][reservation_uuid].uuid];
            Object.keys(this.modified).forEach(key => {
              if (related_lots.has(key)) {
                this.modified[key]['settlement'] = value;
              }
            });
          } else {
            this.modified[reservation_uuid]['settlement'] = value;
          }
        }
      },
      onInvoicePaymentsClick(invoice) {
        this.showInvoicePaymentListModal({
          source: SOURCE_BATCH_MARK_UNSOLD_MODAL,
          invoice,
        });
      },
      onSettlementPaymentsClick(settlement) {
        this.showSettlementPaymentListModal({
          source: SOURCE_BATCH_MARK_UNSOLD_MODAL,
          settlement,
        })
      }
    },
    watch: {
      selectedItems() {
        if (!this.initialized && this.selectedItems.length) {

          this.selectedItems.sort((a, b) => {
            const invoiceA = a.lot && a.lot.financial_item && a.lot.financial_item.invoice && a.lot.financial_item.invoice.invoice_no || '';
            const invoiceB = b.lot && b.lot.financial_item && b.lot.financial_item.invoice && b.lot.financial_item.invoice.invoice_no || '';

            if (invoiceA !== invoiceB) {
                if (invoiceA === '') return 1;
                if (invoiceB === '') return -1;
                return invoiceA.localeCompare(invoiceB);
            }

            const settlementA = a.lot && a.lot.financial_item && a.lot.financial_item.settlement && a.lot.financial_item.settlement.settlement_no || '';
            const settlementB = b.lot && b.lot.financial_item && b.lot.financial_item.settlement && b.lot.financial_item.settlement.settlement_no || '';

            if (settlementA !== settlementB) {
                if (settlementA === '') return 1;
                if (settlementB === '') return -1;
                return settlementA.localeCompare(settlementB);
            }

            const lotNoA = a.lot && a.lot.lot_no || 0;
            const lotNoB = b.lot && b.lot.lot_no || 0;

            return lotNoA - lotNoB;
          });

          for (let reservation of this.selectedItems) {
            const choiceData = {}
            const errorData = {}
            const modifiedData = {}

            if (this.getInvoice(reservation)) {
              const invoice = this.getInvoice(reservation)
              choiceData['invoice_option'] = null
              errorData['invoice'] = {msg: null, state: null}
              modifiedData['invoice'] = false
              if (invoice.uuid in this.invSetToResMap) {
                this.invSetToResMap[invoice.uuid].add(reservation.uuid)
              } else {
                this.invSetToResMap[invoice.uuid] = new Set([reservation.uuid])
              }
              this.resToInvSetMap['invoices'][reservation.uuid] = {uuid: invoice.uuid, total: invoice.total_lots}
              this.resToInvSetMap['invoices'][reservation.uuid]['paidAmount'] = invoice.paid_amount || 0;
              this.disableSubmit = true
            }

            if (this.getSettlement(reservation)) {
              const settlement = this.getSettlement(reservation)
              choiceData['settlement_option'] = null
              errorData['settlement'] = {msg: null, state: null}
              modifiedData['settlement'] = false
              if (settlement.uuid in this.invSetToResMap) {
                this.invSetToResMap[settlement.uuid].add(reservation.uuid)
              } else {
                this.invSetToResMap[settlement.uuid] = new Set([reservation.uuid])
              }
              this.resToInvSetMap['settlements'][reservation.uuid] = {
                uuid: settlement.uuid,
                total: settlement.total_financial_items
              }
              this.resToInvSetMap['settlements'][reservation.uuid]['paidAmount'] = settlement.payments_amount || 0;
              this.disableSubmit = true
            }

            this.data[reservation.uuid] = choiceData
            this.errors[reservation.uuid] = errorData
            this.modified[reservation.uuid] = modifiedData
          }
          this.initialized = true
        }
      },
      'modal.error'() {
        if (this.modal.error !== null) {
          this.$toast('danger', this.modal.error)
        }
      }
    }
  }


</script>

<style scoped lang="scss">
  span {
    text-transform: capitalize;
  }
</style>
